import { ApolloQueryResult } from '@apollo/client';
import { Button, useDisclosure } from '@chakra-ui/react';
import { FaUndo } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { Deal, DealStateEnum, DealStateLabelMap } from '../../gql/dealGql';
import { useDealUpdateStateMutation } from '../../gql/generated/graphql';

import DynamicSizedButton from '../shared/DynamicSizedButton';
import Modal from '../shared/Modal';

import { PermissionEnum } from '../../constants/permissions';
import { Can } from '../../libs/Can';
import { formatDate } from '../../libs/utils';

type UnwindDealToApprovedProps = {
  deal: Deal;
  refetch?: () => Promise<ApolloQueryResult<Deal>>;
};

type UnwindToApprovedDealStateType =
  | DealStateEnum.Structuring
  | DealStateEnum.Closing
  | DealStateEnum.Closed;

export const stateIsUnwindableToApproved = (
  state: DealStateEnum,
  approved_date?: string,
): state is UnwindToApprovedDealStateType =>
  !!approved_date &&
  (state === DealStateEnum.Structuring ||
    state === DealStateEnum.Closing ||
    state === DealStateEnum.Closed);

const UnwindDealToApproved = ({ deal, refetch }: UnwindDealToApprovedProps) => {
  const {
    isOpen: isUnwindModalOpen,
    onOpen: onOpenUnwindModal,
    onClose: onCloseUnwindModal,
  } = useDisclosure();

  const [updateDealState, { loading }] = useDealUpdateStateMutation();

  const onClickUnwindButton = async () => {
    if (!deal.id || !stateIsUnwindableToApproved(deal.state, deal.approved_date)) {
      return;
    }

    try {
      const previousState = DealStateEnum.Structuring;
      await updateDealState({
        variables: {
          id: deal.id,
          state: previousState,
          clearApprovedDate: true,
        },
      });

      toast.success(`Deal moved to ${DealStateLabelMap[previousState]}.`);
      refetch?.();
    } catch (e) {
      toast.error('Failed to unwind deal.');
    } finally {
      onCloseUnwindModal();
    }
  };

  return (
    <Can I={PermissionEnum.UnwindToSetDeal}>
      <DynamicSizedButton
        ariaLabel="unwind-approval"
        variant="boot"
        isRound
        icon={<FaUndo />}
        label="Unwind Approval"
        onClick={onOpenUnwindModal}
        hidden={!stateIsUnwindableToApproved(deal.state, deal.approved_date)}
      />

      <Modal
        title="Unwind Approval"
        isOpen={isUnwindModalOpen}
        onClose={onCloseUnwindModal}
        size="lg"
        headerSize="md"
        canDismiss={false}
        showDivider={false}
        leftButtons={
          <Button
            size="md"
            variant="warning"
            onClick={() => {
              onCloseUnwindModal();
            }}
            isDisabled={loading}
          >
            Cancel
          </Button>
        }
        rightButtons={
          <Button
            size="md"
            variant="boot"
            onClick={() => {
              onClickUnwindButton();
            }}
            isLoading={loading}
            loadingText="Unwind"
          >
            Unwind
          </Button>
        }
      >
        Are you sure you want to unwind approval and clear the approved date?
        <br />
        <b>Approved Date:</b> {formatDate(deal.approved_date)}
      </Modal>
    </Can>
  );
};

export default UnwindDealToApproved;
